/**
 * API 服务模块：封装 HTTP 请求，自动附加 token 认证
 */

const API_BASE_URL: string =
  import.meta.env.VITE_API_BASE_URL

/**
 * localStorage 中存储 token 的 key
 */
const AUTH_TOKEN_KEY = 'auth-token' as const

/**
 * Zustand auth-storage 的 key
 */
const AUTH_STORAGE_KEY = 'auth-storage' as const

/**
 * 401 未授权处理回调类型
 */
type UnauthorizedHandler = () => void

/**
 * 全局 401 处理器
 */
let onUnauthorizedHandler: UnauthorizedHandler | null = null

/**
 * 设置全局 401 未授权处理器
 * @param handler 处理器函数
 */
function setUnauthorizedHandler(handler: UnauthorizedHandler): void {
  onUnauthorizedHandler = handler
}

/**
 * 清除本地认证状态（token 和 auth storage）
 */
function clearAuthState(): void {
  try {
    localStorage.removeItem(AUTH_TOKEN_KEY)
    localStorage.removeItem(AUTH_STORAGE_KEY)
  } catch {
    // localStorage 不可用时静默失败
  }
}

interface RequestConfig {
  method: string
  headers?: Record<string, string>
  body?: BodyInit | null
  params?: Record<string, string>
}

interface HttpError extends Error {
  status: number
  statusText: string
  code?: number
  apiMessage?: string
}

/**
 * 创建 HTTP 错误对象
 * @param status HTTP 状态码
 * @param statusText 状态描述
 * @param apiMessage API 返回的错误消息（优先显示）
 * @param code API 返回的错误码
 * @returns HttpError 对象
 */
function createHttpError(
  status: number,
  statusText: string,
  apiMessage?: string,
  code?: number
): HttpError {
  const message = apiMessage ?? `HTTP error! status: ${String(status)}`
  const error = new Error(message) as HttpError
  error.status = status
  error.statusText = statusText
  if (code !== undefined) {
    error.code = code
  }
  if (apiMessage !== undefined) {
    error.apiMessage = apiMessage
  }
  return error
}

/**
 * 从 localStorage 获取 token
 * @returns token 字符串或 null
 */
function getAuthToken(): string | null {
  try {
    return localStorage.getItem(AUTH_TOKEN_KEY)
  } catch {
    return null
  }
}

class ApiService {
  private readonly baseUrl: string

  public constructor(baseUrl: string) {
    this.baseUrl = baseUrl
  }

  /**
   * 构建完整 URL
   * @param endpoint API 端点
   * @param params 查询参数
   * @returns 完整 URL
   */
  private buildUrl(endpoint: string, params?: Record<string, string>): string {
    // 支持相对路径（开发环境代理）和完整 URL（生产环境）
    const fullPath = `${this.baseUrl}${endpoint}`
    const url: URL = fullPath.startsWith('http')
      ? new URL(fullPath)
      : new URL(fullPath, window.location.origin)

    if (params !== undefined) {
      Object.entries(params).forEach(([key, value]: [string, string]): void => {
        url.searchParams.append(key, value)
      })
    }
    return url.toString()
  }

  /**
   * 发送 HTTP 请求
   * @param endpoint API 端点
   * @param config 请求配置
   * @returns 响应数据
   */
  public async request<T>(endpoint: string, config: RequestConfig): Promise<T> {
    const { params, body, headers, method } = config
    const url: string = this.buildUrl(endpoint, params)

    const mergedHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      ...headers,
    }

    // 自动附加 token 到 Authorization header
    const token: string | null = getAuthToken()
    if (token !== null) {
      mergedHeaders['Authorization'] = `Bearer ${token}`
    }

    const requestInit: RequestInit = {
      method,
      headers: mergedHeaders,
    }

    if (body !== undefined && body !== null) {
      requestInit.body = body
    }

    const response: Response = await fetch(url, requestInit)

    if (!response.ok) {
      // 处理 401 未授权错误：清除本地状态并触发处理器
      if (response.status === 401) {
        clearAuthState()
        if (onUnauthorizedHandler !== null) {
          onUnauthorizedHandler()
        }
      }

      // 尝试解析错误响应体获取详细错误信息
      try {
        const errorData = (await response.json()) as {
          code?: number
          message?: string
        }
        throw createHttpError(
          response.status,
          response.statusText,
          errorData.message,
          errorData.code
        )
      } catch (parseError) {
        // 如果解析失败或 parseError 已经是 HttpError，则抛出
        if (parseError instanceof Error && 'status' in parseError) {
          throw parseError
        }
        throw createHttpError(response.status, response.statusText)
      }
    }

    const data: T = (await response.json()) as T
    return data
  }

  /**
   * GET 请求
   * @param endpoint API 端点
   * @param params 查询参数
   * @returns 响应数据
   */
  public async get<T>(
    endpoint: string,
    params?: Record<string, string>
  ): Promise<T> {
    const config: RequestConfig = { method: 'GET' }
    if (params !== undefined) {
      config.params = params
    }
    return await this.request<T>(endpoint, config)
  }

  /**
   * POST 请求
   * @param endpoint API 端点
   * @param data 请求体数据
   * @returns 响应数据
   */
  public async post<T>(endpoint: string, data?: unknown): Promise<T> {
    const config: RequestConfig = { method: 'POST' }
    if (data !== undefined) {
      config.body = JSON.stringify(data)
    }
    return await this.request<T>(endpoint, config)
  }

  /**
   * PUT 请求
   * @param endpoint API 端点
   * @param data 请求体数据
   * @returns 响应数据
   */
  public async put<T>(endpoint: string, data?: unknown): Promise<T> {
    const config: RequestConfig = { method: 'PUT' }
    if (data !== undefined) {
      config.body = JSON.stringify(data)
    }
    return await this.request<T>(endpoint, config)
  }

  /**
   * DELETE 请求
   * @param endpoint API 端点
   * @returns 响应数据
   */
  public async delete<T>(endpoint: string): Promise<T> {
    return await this.request<T>(endpoint, { method: 'DELETE' })
  }
}

export const apiService: ApiService = new ApiService(API_BASE_URL)

export { AUTH_TOKEN_KEY, setUnauthorizedHandler }
export type { RequestConfig, HttpError }
